import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Mail, Lock, ArrowLeft, CheckCircle, AlertCircle, Loader2 } from "lucide-react";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [step, setStep] = useState(token ? 'reset' : 'request'); // request, reset, success
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tokenValid, setTokenValid] = useState(null);

  // Verifica token se presente
  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      await axios.get(`${API}/auth/verify-reset-token/${token}`);
      setTokenValid(true);
    } catch (error) {
      setTokenValid(false);
      toast.error("Link non valido o scaduto");
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Inserisci la tua email");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success("Se l'email esiste, riceverai le istruzioni");
      setStep('sent');
    } catch (error) {
      toast.error("Errore nell'invio della richiesta");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    
    if (password.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setStep('success');
      toast.success("Password aggiornata con successo");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel reset della password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/20">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              I CIRILLO PARRUCCHIERI
            </h1>
            <p className="text-blue-200/70 text-sm">Recupero Password</p>
          </div>

          {/* Step: Richiesta email */}
          {step === 'request' && (
            <form onSubmit={handleRequestReset} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-blue-100 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-300/50" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="La tua email"
                    className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-blue-400"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Invia istruzioni"
                )}
              </Button>

              <Link
                to="/login"
                className="flex items-center justify-center gap-2 text-blue-300 hover:text-white transition-colors text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Torna al login
              </Link>
            </form>
          )}

          {/* Step: Email inviata */}
          {step === 'sent' && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-8 h-8 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Controlla la tua email</h2>
                <p className="text-blue-200/70 text-sm">
                  Se l'indirizzo è registrato, riceverai le istruzioni per reimpostare la password.
                </p>
              </div>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-blue-300 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Torna al login
              </Link>
            </div>
          )}

          {/* Step: Reset password (con token) */}
          {step === 'reset' && (
            <>
              {tokenValid === null && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
                </div>
              )}

              {tokenValid === false && (
                <div className="text-center space-y-6">
                  <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
                    <AlertCircle className="w-8 h-8 text-red-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white mb-2">Link non valido</h2>
                    <p className="text-blue-200/70 text-sm">
                      Il link è scaduto o già utilizzato. Richiedi un nuovo link di reset.
                    </p>
                  </div>
                  <Button
                    onClick={() => { setStep('request'); navigate('/reset-password'); }}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Richiedi nuovo link
                  </Button>
                </div>
              )}

              {tokenValid === true && (
                <form onSubmit={handleResetPassword} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-blue-100 mb-2">
                      Nuova Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-300/50" />
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Almeno 6 caratteri"
                        className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-blue-400"
                        required
                        minLength={6}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-blue-100 mb-2">
                      Conferma Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-300/50" />
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Ripeti la password"
                        className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-blue-400"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      "Aggiorna Password"
                    )}
                  </Button>
                </form>
              )}
            </>
          )}

          {/* Step: Successo */}
          {step === 'success' && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Password aggiornata!</h2>
                <p className="text-blue-200/70 text-sm">
                  La tua password è stata aggiornata con successo.
                </p>
              </div>
              <Button
                onClick={() => navigate('/login')}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Vai al Login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
